import { useState, useEffect, useCallback } from 'react';
import {
    isS3NotFoundError,
    LATEST_PACKAGE_FOLDER,
    type PackageDetails,
    packageFactory,
} from '../services/Clients/S3Client';
import ContextualBranchManager from '../services/Dom/ContextualBranchManager';
import { type PackageTree } from '../services/Clients/PackageTree';

interface UseContextualS3ValidationProps {
    label: string;
    defaultSelection: string;
    packageTreeInstance: PackageTree;
    onArtifactSelect?: (artifactName: string, artifactVersion: string) => void;
}

export const useContextualS3Validation = ({
    label,
    defaultSelection,
    packageTreeInstance,
    onArtifactSelect,
}: UseContextualS3ValidationProps) => {
    const [validatedBranch, setValidatedBranch] = useState<PackageDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [validationError, setValidationError] = useState<Error | null>(null);
    const [retryAttempt, setRetryAttempt] = useState(0);

    const getContextualBranchId = useCallback(() => {
        return ContextualBranchManager.getSelectedBranch(label) || defaultSelection;
    }, [label, defaultSelection]);

    const getContextualVersionId = useCallback(() => {
        return ContextualBranchManager.getSelectedVersion(label) || LATEST_PACKAGE_FOLDER;
    }, [label]);

    const notifyOfArtifactSelection = useCallback((projectedBranch: string, projectedVersion: string) => {
        if (onArtifactSelect) {
            onArtifactSelect(projectedBranch, projectedVersion);
        }
    }, [onArtifactSelect]);

    const fallbackToDefaultPackage = useCallback((invalidBranch: string, invalidVersion: string) => {
        const defaultBranch = packageFactory(defaultSelection);
        const defaultVersion = LATEST_PACKAGE_FOLDER;
        const invalidUrlBranch = ContextualBranchManager.getUrlValueFromBranch(invalidBranch);
        const defaultUrlBranch = ContextualBranchManager.getUrlValueFromBranch(defaultBranch.folder);

        if (invalidUrlBranch === defaultUrlBranch && invalidVersion === defaultVersion) {
            throw new Error(`The default S3 selection "${defaultBranch.folder}" could not be validated.`);
        }

        ContextualBranchManager.setSelectedBranch(defaultBranch.folder, label);
        ContextualBranchManager.setSelectedVersion(defaultVersion, label);
        ContextualBranchManager.replaceSelectionInUrl(defaultBranch.folder, defaultVersion, label);
        window.location.reload();
    }, [defaultSelection, label]);

    const validateInitialSelection = useCallback(async (isCancelled: () => boolean) => {
        const projectedBranchId = getContextualBranchId();
        const projectedVersionId = getContextualVersionId();

        try {
            if (projectedVersionId === LATEST_PACKAGE_FOLDER) {
                const requestedBranch = packageFactory(projectedBranchId);
                const versions = await packageTreeInstance.fetchVersionTreeInformation(requestedBranch);
                const latestVersion = versions[0];

                if (latestVersion) {
                    const branch = packageTreeInstance.getBranchByFolder(projectedBranchId) ?? requestedBranch;
                    if (!isCancelled()) {
                        setValidatedBranch(branch);
                        notifyOfArtifactSelection(projectedBranchId, latestVersion.folder);
                    }
                    return;
                }
            } else {
                const selectionExists = await packageTreeInstance.selectionExists(projectedBranchId, projectedVersionId);
                if (selectionExists && !isCancelled()) {
                    setValidatedBranch(packageFactory(projectedBranchId));
                    notifyOfArtifactSelection(projectedBranchId, projectedVersionId);
                    return;
                }
            }
        } catch (error) {
            if (!isS3NotFoundError(error)) {
                throw error;
            }
        }

        if (!isCancelled()) {
            fallbackToDefaultPackage(projectedBranchId, projectedVersionId);
        }
    }, [
        fallbackToDefaultPackage,
        getContextualBranchId,
        getContextualVersionId,
        notifyOfArtifactSelection,
        packageTreeInstance,
    ]);

    useEffect(() => {
        let cancelled = false;

        const loadContext = async () => {
            setValidationError(null);
            setIsLoading(true);

            try {
                await validateInitialSelection(() => cancelled);
                if (!cancelled) {
                    setIsLoading(false);
                }
            } catch (error) {
                if (!cancelled) {
                    setValidationError(error instanceof Error ? error : new Error('Unable to validate the S3 selection.'));
                    setIsLoading(false);
                }
            }
        };


        void loadContext();

        return () => {
            cancelled = true;
        };
    }, [retryAttempt, validateInitialSelection]);

    const retry = useCallback(() => {
        setRetryAttempt(attempt => attempt + 1);
    }, []);

    return {
        currentBranch: validatedBranch,
        unvalidatedBranchName: getContextualBranchId(),
        unvalidatedVersionName: getContextualVersionId(),
        isLoading,
        validationError,
        retry,
    };
};
